import { WPPost } from '../types/wordpress';
import { ContentType, Audience, ClinicalArea, Topic, ResourceMetadata } from '../types/metadata';
import { decodeHtmlEntities } from '../utils/htmlEntities';

/**
 * Mock metadata service to assign test metadata to WordPress posts
 * This simulates metadata that will eventually come from WordPress custom fields or a separate API
 */

// Helper function to get a deterministic value based on post ID
function getDeterministicValue<T>(array: T[], seed: number): T {
  return array[seed % array.length];
}

// Helper function to get multiple deterministic values
function getDeterministicValues<T>(array: T[], seed: number, count: number): T[] {
  const values: T[] = [];
  for (let i = 0; i < count; i++) {
    values.push(array[(seed + i) % array.length]);
  }
  return [...new Set(values)]; // Remove duplicates
}

/**
 * Assigns mock metadata to a WordPress post based on its ID
 * This creates deterministic metadata so the same post always gets the same metadata
 */
export function assignMockMetadata(post: Pick<WPPost, 'id'> & { title: { rendered: string } }): ResourceMetadata {
  const seed = post.id;
  
  // Content type - assign based on post title/keywords
  const titleLower = decodeHtmlEntities(post.title.rendered).toLowerCase();
  let contentType: ContentType;
  
  if (titleLower.includes('toolkit') || titleLower.includes('guideline')) {
    contentType = ContentType.Toolkit_Guideline;
  } else if (titleLower.includes('template') || titleLower.includes('form')) {
    contentType = ContentType.Template_Form;
  } else if (titleLower.includes('training') || titleLower.includes('education') || titleLower.includes('elearning')) {
    contentType = ContentType.Educational_Training;
  } else if (titleLower.includes('audit') || titleLower.includes('report')) {
    contentType = ContentType.Audit_Report;
  } else if (titleLower.includes('event') || titleLower.includes('webinar') || titleLower.includes('symposium')) {
    contentType = ContentType.Event_Webinar;
  } else if (titleLower.includes('article') || titleLower.includes('publication')) {
    contentType = ContentType.Publication_Article;
  } else if (titleLower.includes('portal') || titleLower.includes('landing')) {
    contentType = ContentType.Landing_Portal;
  } else {
    contentType = getDeterministicValue(
      Object.values(ContentType),
      seed
    );
  }

  // Audiences - assign 1-3 audiences
  const allAudiences = Object.values(Audience).filter(a => a !== Audience.Unknown && a !== Audience.Other);
  const audiences = getDeterministicValues(allAudiences, seed, 1 + (seed % 3));

  // Clinical areas - assign 1-2 areas
  const allClinicalAreas = Object.values(ClinicalArea).filter(
    a => a !== ClinicalArea.Unknown && a !== ClinicalArea.Other
  );
  const clinicalAreas = getDeterministicValues(allClinicalAreas, seed * 2, 1 + (seed % 2));

  // Topics - assign 2-5 topics
  const allTopics = Object.values(Topic).filter(t => t !== Topic.Unknown && t !== Topic.Other);
  const topics = getDeterministicValues(allTopics, seed * 3, 2 + (seed % 4));

  return {
    contentType,
    audiences,
    clinicalAreas,
    topics,
  };
}

/**
 * Gets metadata for a post (mock data for now)
 * In production, this would fetch from WordPress custom fields or a separate API
 */
export function getPostMetadata(post: Pick<WPPost, 'id'> & { title: { rendered: string } }): ResourceMetadata {
  return assignMockMetadata(post);
}

/**
 * Gets metadata for multiple posts
 */
export function getPostsMetadata(posts: WPPost[]): Map<number, ResourceMetadata> {
  const metadataMap = new Map<number, ResourceMetadata>();
  posts.forEach(post => {
    metadataMap.set(post.id, assignMockMetadata(post));
  });
  return metadataMap;
}

